import React from 'react';
import * as PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { CommonButtonSize, CommonButtonVariants } from '../../constants/CommonButtonVariants';
import { AuctionStatus } from '../../constants/AuctionStatus';

const variantClassMap = {
  [CommonButtonVariants.PRIMARY]: 'primary-button',
  [CommonButtonVariants.PRIMARY_RED]: 'primary-red-button',
  [CommonButtonVariants.SECONDARY]: 'secondary-button',
  [CommonButtonVariants.DISABLED]: 'disabled-button',
  [CommonButtonVariants.ORGANIZATION_NAV]: 'my-organization-nav-link',
  [CommonButtonVariants.AUCTION]: 'tertiary-button',
  [CommonButtonVariants.SUCCESS]: 'success-button',
  [CommonButtonVariants.DANGER]: 'btn-danger',
  [CommonButtonVariants.GREY]: 'btn-grey',
  [CommonButtonVariants.PUBLISH]: 'publish-auction-button',
  [CommonButtonVariants.CAR_FEATURE]: 'car-feature-button',
  [CommonButtonVariants.CAR_FEATURE_SELECTED]: 'car-feature-button--selected',
  [CommonButtonVariants.DELETE]: 'delete-button',
  [CommonButtonVariants.OUTLINED]: 'outline-primary',
  [CommonButtonVariants.RED]: 'red-button',
  [CommonButtonVariants.OUTLINED_RED]: 'outline-primary-red',
  [CommonButtonVariants.OUTLINED_WHITE]: 'outline-primary-white',
  [CommonButtonVariants.LIVE]: 'live-button',
  [CommonButtonVariants.SOLD]: 'sold-button',
  [CommonButtonVariants.CANCELLED]: 'cancelled-button',
  [CommonButtonVariants.DRAFT]: 'draft-button',
};

const sizeClassMap = {
  [CommonButtonSize.LARGE]: 'common-button--large',
  [CommonButtonSize.SMALL]: 'common-button--small',
  [CommonButtonSize.MEDIUM]: 'common-button--medium',
};

const colorClassMap = {
  [AuctionStatus.WAITING]: 'common-button--waiting',
  [AuctionStatus.PUBLISHED]: 'common-button--published',
  [AuctionStatus.REJECTED]: 'common-button--rejected',
  [AuctionStatus.TO_ACCEPT]: 'common-button--to-accept',
  [AuctionStatus.SOLD]: 'common-button--sold',
  [AuctionStatus.CANCELLED]: 'common-button--canceled',
  [AuctionStatus.ENDED]: 'common-button--ended',
};

function CommonButton({
  label,
  variant,
  className,
  handleClick,
  type,
  disabled,
  size,
  navLink,
  path,
  icon,
  iconAlt,
  iconLeft,
  color,
  buttonProperties,
}) {
  const variantClass = variantClassMap[variant] || 'primary-button';
  const sizeClass = sizeClassMap[size] || '';
  const colorClass = colorClassMap[color] || '';

  const checkActive = (match, location) => {
    if (!location) return false;
    return location.pathname === path;
  };

  if (navLink) {
    return (
      <NavLink
        to={path}
        className={classNames('common-nav-link', variantClass, sizeClass, className)}
        activeClassName="common-nav-link--active"
        isActive={checkActive}
      >
        {label}
      </NavLink>
    );
  }

  return (
    <button
      /* eslint-disable-next-line react/button-has-type */
      type={type}
      className={classNames('common-button', variantClass, sizeClass, colorClass, className)}
      onClick={handleClick}
      disabled={disabled}
      {...buttonProperties}
    >
      <div className={classNames('common-button__content', iconLeft && 'flex-row-reverse')}>
        <span>{label}</span>
        {icon && iconAlt && (
          <img
            className="common-button__icon"
            src={icon}
            alt={iconAlt}
            style={{
              filter: disabled ? 'grayscale(1)' : 'grayscale(0)',
              opacity: disabled ? '0.5' : '1',
            }}
          />
        )}
      </div>
    </button>
  );
}

CommonButton.defaultProps = {
  variant: CommonButtonVariants.PRIMARY,
  className: '',
  type: 'button',
  disabled: false,
  size: CommonButtonSize.DEFAULT,
  navLink: false,
  path: '',
  handleClick: null,
  icon: '',
  iconAlt: '',
  color: '',
  iconLeft: false,
  buttonProperties: {},
};

CommonButton.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  variant: PropTypes.string,
  className: PropTypes.string,
  handleClick: PropTypes.func,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  navLink: PropTypes.bool,
  path: PropTypes.string,
  icon: PropTypes.string,
  iconAlt: PropTypes.string,
  color: PropTypes.string,
  iconLeft: PropTypes.bool,
  buttonProperties: PropTypes.object,
};

export default CommonButton;
